import React, { useContext, useEffect, useState } from "react";
import { api as Api } from "api/http";
import { SessionContext } from "./SessionContext";
import { Notification, toast } from "components/ui";
import AppSumoModal from "components/layout/AppSumoModal";
import AppSumoWelcomeModal from "components/layout/AppSumoWelcomeModal";
import AppSumoReviewModal from "components/layout/AppSumoReviewModal";
import AppSumoLicenseModal from "components/layout/AppSumoLicenseModal";
import { CompanyContext } from "./CompanyContext";
import { ModalContext } from "./ModalContext";
import PlusModal from "components/shared/PlusModal";
import FreePlusModal from "components/shared/FreePlusModal";
import { PriorityModalContext } from "./PriorityModalContext";
import { useWhitelabel } from "contexts/WhitelabelThemeContext";

const AppSumoContext = React.createContext({
    showAppSumoModal: false,
    setShowAppSumoModal: (value) => void 0,
    showAppSumoWelcomeModal: false,
    setShowAppSumoWelcomeModal: (value) => void 0,
    showAppSumoReviewModal: false,
    setShowAppSumoReviewModal: (value) => void 0,
    setShowFreePremiumImageModal: (value) => void 0,
    hasPremiumImages: () => true,
    hasPremiumText: () => true,
    isSumoling: () => false,
    isPlus: () => false,
    getTier: () => 0,
    isLicensee: () => false,
    getLicenseTier: () => 0,
    showAppSumoLicenseModal: false,
    setShowAppSumoLicenseModal: (value) => void 0,
});

// --- Dev-only AppSumo state overrides ---
// Drive the context with URL params so you can simulate sumoling/licensee states
// without editing code or seeding the DB. Flags persist in localStorage.
//
//   ?fakeSumoling=1                 → isSumoling()=true (+ getTier defaults to 3)
//   ?fakeLicensee=1                 → isLicensee()=true (+ getLicenseTier defaults to 1)
//   ?fakeTier=N                     → getTier()=N        (1..APPSUMO_MAX_TIER)
//   ?fakeLicenseTier=N              → getLicenseTier()=N (1..3)
//   ?fakePlus=1                     → isPlus()=true
//   ?clearFakeAppSumo=1             → clear every override
//
// Reads the URL on every page load and writes through to localStorage so the
// override survives client-side navigation. No-op outside of NODE_ENV=development.
const FAKE_APPSUMO_KEY = 'devAppSumoFakes';
const readFakeAppSumoFlags = () => {
    if (typeof window === 'undefined') return {};
    if (process.env.NODE_ENV === 'production') return {};
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(FAKE_APPSUMO_KEY) || '{}') || {}; } catch (_) { stored = {}; }
    try {
        const params = new URL(window.location.href).searchParams;
        if (params.has('clearFakeAppSumo')) {
            localStorage.removeItem(FAKE_APPSUMO_KEY);
            return {};
        }
        const merge = (key, parser) => {
            if (params.has(key)) stored[key] = parser(params.get(key));
        };
        merge('fakeSumoling',      v => v === '1');
        merge('fakeLicensee',      v => v === '1');
        merge('fakePlus',          v => v === '1');
        merge('fakeTier',          v => parseInt(v, 10));
        merge('fakeLicenseTier',   v => parseInt(v, 10));
        if (params.has('fakeSumoling') || params.has('fakeLicensee') || params.has('fakePlus') ||
            params.has('fakeTier') || params.has('fakeLicenseTier')) {
            localStorage.setItem(FAKE_APPSUMO_KEY, JSON.stringify(stored));
        }
    } catch (_) {}
    return stored;
};

const AppSumoProvider = ({ children }) => {

    const { isWhitelabel } = useWhitelabel();
    const { openModalPriority } = useContext(PriorityModalContext);
    const { company, companyUsers, mainUser } = useContext(CompanyContext);
    const { updateUserData, user } = useContext(SessionContext);
    const fakes = readFakeAppSumoFlags();
    const [ showAppSumoWelcomeModal, setShowAppSumoWelcomeModal ] = useState(false);
    const [ showAppSumoModal, setShowAppSumoModal ] = useState(false);
    const [ showAppSumoReviewModal, setShowAppSumoReviewModal ] = useState(false);
    const [ showAppSumoLicenseModal, setShowAppSumoLicenseModal ] = useState(false);
    const [ showFreePremiumImageModal, setShowFreePremiumImageModal ] = useState(false);
    const [ hasShownFreePremiumImagesModal, setHasShownFreePremiumImagesModal ] = useState(!!localStorage.getItem(`hasShownFreePremiumImagesModal`))
    const [ hasShownAppSumoReviewModal, setHasShownAppSumoReviewModal ] = useState(!!localStorage.getItem(`hasShownAppSumoReviewModal`))
    const [ lastPremiumImagesSpentBalance, setLastPremiumImagesSpentBalance ] = useState(user?.balance?.premium_images_spent);
    const [ plan, setPlan ] = useState();

    const getTier = () => {
        if (Number.isFinite(fakes.fakeTier)) return fakes.fakeTier;
        if (fakes.fakeSumoling) return 3;
        if (!!companyUsers?.length) {
            if (mainUser) {
                return mainUser?.subscriptionTokens?.length;
            }
        }
        return 0;
    }

    const isPlus = () => {
        if (typeof fakes.fakePlus === 'boolean') return fakes.fakePlus;
        if (fakes.fakeSumoling) return false;
        if (!!companyUsers?.length) {
            if (mainUser) {
                return !!mainUser?.subscription?.items?.find(i => i?.product?.name == `followr-plus`) || isLicensee();
            }
        }
        return false;
    }

    const isPlusText = () => {
        if (fakes.fakeSumoling) return false;
        if (user?.balance) {
            return !!user?.balance?.plus_chat_enabled;
        }
        return true;
    }

    const hasSubscription = () => {
        if (fakes.fakeSumoling) return true;
        if (!!companyUsers?.length) {
            if (mainUser) {
                return !!mainUser?.subscription?.items?.length;
            }
        }
        return false;
    }

    const hasFreeSubscription = () => {
        if (fakes.fakeSumoling) return true;
        if (!!companyUsers?.length) {
            if (mainUser) {
                return !!mainUser?.subscription?.items?.find(i => i?.product?.name == `free`);
            }
        }
        return false;
    }

    const isSumoling = () => {
        if (fakes.fakeSumoling) return true;
        if (!!companyUsers?.length) {
            return !!getTier() && !isPlus() && !!hasSubscription() && !!hasFreeSubscription() && !isLicensee();
        }
        return false;
    }

    const isLicensee = () => {
        if (fakes.fakeLicensee) return true;
        if (mainUser) {
            return !!mainUser?.appSumoLicense;
        }
        return false;
    }

    const getLicenseTier = () => {
        if (Number.isFinite(fakes.fakeLicenseTier)) return fakes.fakeLicenseTier;
        if (fakes.fakeLicensee) return 1;
        if (mainUser?.appSumoLicense) {
            return mainUser.appSumoLicense.tier;
        }
        return 0;
    }

    const hasPremiumImages = () => {
        if (fakes.fakeSumoling) return false;
        if (!isSumoling()) return true;
        return (user?.balance?.premium_images_allowed || 0) - (user?.balance?.premium_images_spent || 0) > 0;
    }

    const hasPremiumText = () => {
        if (fakes.fakeSumoling) return false;
        if (!isSumoling()) return true;
        if (!isPlusText()) return false;
        return true;
    }

	useEffect(() => {
		if (localStorage.getItem(`appsumo`)) {
            if (mainUser?.id) {
                const appsumo = localStorage.getItem(`appsumo`);
                localStorage.removeItem(`appsumo`);
                Api.post(`/subscriptionTokens/redeem?include=items.product`, {}, { subscription_token: appsumo }).then((response) => {
                    setShowAppSumoModal(true);
                    setPlan(response?.data?.items?.[0]?.product?.label);
                    updateUserData();
                    window.Intercom('update', {
                        "subscription_token_plan": response?.data?.items?.[0]?.product?.name
                    });
                }).catch((err) => {
                    if (String(err?.message)?.includes(".exists")) {
                        toast.push(<Notification type="warning" title={`AppSumo: the redemption code doesn't exist. Try clicking the redeem button in AppSumo.`} />, { placement: "top-center" });
                    } else if (String(err?.message)?.includes("No query results for model")) {
                        toast.push(<Notification type="warning" title={`AppSumo: Your code has already been redeemed.`} />, { placement: "top-center" });
                    } else {
                        toast.push(<Notification type="warning" title={`AppSumo: ${err}`} />, { placement: "top-center" });
                    }
                })
            }
        } 
	}, [mainUser?.subscription, mainUser?.id]);

    useEffect(() => {
        if (
            !!isSumoling() && 
            user?.balance?.premium_images_spent >= user?.balance?.premium_images_allowed && 
            lastPremiumImagesSpentBalance !== undefined && 
            lastPremiumImagesSpentBalance < user?.balance?.premium_images_spent && 
            !isWhitelabel
        ) {
            openModalPriority(<PlusModal />, 650, true, true, "relative !overflow-visible !overflow-y-visible !p-0 !bg-transparent");
        }
        setLastPremiumImagesSpentBalance(user?.balance?.premium_images_spent);
    }, [user?.balance?.premium_images_spent]);

    useEffect(() => {
        if (!!companyUsers?.length) {
            if (
                !hasShownFreePremiumImagesModal && 
                !!isSumoling() && 
                user?.balance?.premium_images_allowed > user?.balance?.premium_images_spent && 
                user?.balance?.images_spent >= 5 && 
                !user?.balance?.premium_images_spent
            ) {
                setShowFreePremiumImageModal(true);
                setHasShownFreePremiumImagesModal(true);
                localStorage.setItem(`hasShownFreePremiumImagesModal`, `1`);
            }
        }
    }, [companyUsers?.length, user?.balance?.images_spent]);

    // Show AppSumo Review Modal after 20 seconds for Sumolings who have created companies
    useEffect(() => {
        if (
            !hasShownAppSumoReviewModal && 
            !!isSumoling() && 
            !!company?.id && 
            !isWhitelabel &&
            companyUsers?.length > 0 && 
            mainUser?.id == user?.id && 
            new Date(user?.created_at) < new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 5) // 5 days
        ) {
            const timer = setTimeout(() => {
                setShowAppSumoReviewModal(true);
                setHasShownAppSumoReviewModal(true);
                localStorage.setItem(`hasShownAppSumoReviewModal`, `1`);
            }, 10000); // 10 seconds

            return () => clearTimeout(timer);
        }
    }, [hasShownAppSumoReviewModal, isSumoling, company?.id, isWhitelabel, companyUsers?.length]);

    const url = new URL(window.location.href);
    const appsumo = url.searchParams.get("appsumo");
    if (appsumo && window.location.pathname != "/appsumo") {
        window.location.href = `/appsumo?code=${appsumo}`;
        return;
    }

    const appsumoLicenseKey = url.searchParams.get("appsumo_license_key");
    if (appsumoLicenseKey && window.location.pathname != "/appsumo-license") {
        window.location.href = `/appsumo-license?license_key=${appsumoLicenseKey}`;
        return;
    }

    const appsumoError = url.searchParams.get("appsumo_error");
    if (appsumoError && window.location.pathname != "/appsumo-license") {
        window.location.href = `/appsumo-license?error=${appsumoError}`;
        return;
    }

    return (
        <AppSumoContext.Provider value={{
            showAppSumoModal,
            setShowAppSumoModal,
            showAppSumoWelcomeModal,
            setShowAppSumoWelcomeModal,
            showAppSumoReviewModal,
            setShowAppSumoReviewModal,
            setShowFreePremiumImageModal,
            hasPremiumImages,
            hasPremiumText,
            isSumoling,
            isPlus,
            getTier,
            isLicensee,
            getLicenseTier,
            showAppSumoLicenseModal,
            setShowAppSumoLicenseModal,
        }}>
            {children}
            {showAppSumoModal && <AppSumoModal plan={plan} />}
            {showAppSumoWelcomeModal && <AppSumoWelcomeModal />}
            {showAppSumoReviewModal && !isWhitelabel && <AppSumoReviewModal />}
            {showFreePremiumImageModal && !isWhitelabel && <FreePlusModal />}
            {showAppSumoLicenseModal && <AppSumoLicenseModal />}
        </AppSumoContext.Provider>
    )
}

export { AppSumoContext, AppSumoProvider };